/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DeviceModelAdvancedSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Device Models module.
----------------------------------------------------------------*/

import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Keyboard, Clock, Sun } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';
import { DeviceModelFormData } from '../types';

interface DeviceModelAdvancedSectionProps {
  control: Control<DeviceModelFormData>;
  availableDssOptions: { value: string; label: string }[];
  showDssModal: boolean;
  setShowDssModal: (v: boolean) => void;
  showTimezoneModal: boolean;
  setShowTimezoneModal: (v: boolean) => void;
  showTimeFormatModal: boolean;
  setShowTimeFormatModal: (v: boolean) => void;
  showDateFormatModal: boolean;
  setShowDateFormatModal: (v: boolean) => void;
  showLanguageModal: boolean;
  setShowLanguageModal: (v: boolean) => void;
  showSummerTimeModal: boolean;
  setShowSummerTimeModal: (v: boolean) => void;
  showDaylightModal: boolean;
  setShowDaylightModal: (v: boolean) => void;
  showSummerTime: boolean;
  showDaylightSavings: boolean;
}

export function DeviceModelAdvancedSection({
  control, availableDssOptions,
  showDssModal, setShowDssModal,
  showTimezoneModal, setShowTimezoneModal,
  showTimeFormatModal, setShowTimeFormatModal,
  showDateFormatModal, setShowDateFormatModal,
  showLanguageModal, setShowLanguageModal,
}: DeviceModelAdvancedSectionProps) {
  return (
    <FormBox title="Advanced Options" icon={<Keyboard className="h-5 w-5" />}>

      {/* Supported DSS Key Types */}
      <Controller control={control} name="supportedDssTypes" render={({ field }) => {
        const dssTypes: string[] = (() => { try { const p = JSON.parse(field.value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; } })();
        const modalItems = availableDssOptions.map(opt => ({ id: opt.value, name: opt.label }));
        const availableIds = new Set(modalItems.map(i => i.id));
        const validDssTypes = dssTypes.filter(t => availableIds.has(t));
        return (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[180px]">Supported DSS Key Types</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap flex-shrink-0">{validDssTypes.length}/{availableDssOptions.length}</span>
              <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {validDssTypes.length > 0 ? validDssTypes.map((type, idx) => (
                  <span key={`${type}-${idx}`} className="inline-block px-2 py-0.5 text-xs bg-primary/10 text-primary rounded border border-primary/20 whitespace-nowrap flex-shrink-0">{type}</span>
                )) : <span className="text-xs text-muted-foreground italic whitespace-nowrap">None selected</span>}
              </div>
              <button type="button" onClick={() => setShowDssModal(true)}
                className="px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-md border border-primary/30 transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                <Keyboard className="h-3.5 w-3.5" />Edit
              </button>
            </div>
            <MultiSelectModal title="Select DSS Key Types" description="Choose the DSS key functions supported by this phone model"
              items={modalItems} selectedIds={validDssTypes}
              onSelectionChange={(selected) => field.onChange(JSON.stringify(selected))}
              onClose={() => setShowDssModal(false)} isOpen={showDssModal}
              searchPlaceholder="Search DSS key types..." icon={Keyboard} />
          </>
        );
      }} />

      {/* Supported Timezones */}
      <Controller control={control} name="timezoneOptions" render={({ field }) => {
        const tzData: { key: string; label?: string; value?: string; selected: boolean }[] = (() => {
          try { const p = JSON.parse(field.value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; }
        })();
        const selectedIds = tzData.filter(tz => tz.selected !== false).map(tz => tz.key);
        const modalItems = tzData.map(opt => {
          const displayName = opt.label || (opt.key.includes('/') ? opt.key.split('/').pop() : opt.key);
          let offsetStr = '';
          if (opt.value) {
            const val = opt.value.toString();
            if (val === '0' || val === '+0' || val === '+0:00') offsetStr = '(GMT)';
            else if (val.startsWith('-') || val.startsWith('+')) offsetStr = `(GMT${val})`;
            else offsetStr = `(GMT+${val})`;
          }
          return { id: opt.key, name: `${displayName} ${offsetStr}`.trim() };
        });
        const handleChange = (newIds: string[]) => {
          const s = new Set(newIds);
          field.onChange(JSON.stringify(tzData.map(item => ({ ...item, selected: s.has(item.key) }))));
        };
        return (
          <>
            <div className="flex items-center gap-2 mt-4 py-2 border-t border-border/50">
              <span className="text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[180px]">Supported Timezones</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap flex-shrink-0">{selectedIds.length}/{tzData.length}</span>
              <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {selectedIds.length > 0 ? selectedIds.map((tz, idx) => (
                  <span key={`tz-${idx}`} className="inline-block px-2 py-0.5 text-xs bg-sky-500/10 text-sky-600 dark:text-sky-400 rounded border border-sky-500/20 whitespace-nowrap flex-shrink-0">{tz.split('/').pop()}</span>
                )) : tzData.length === 0
                  ? <span className="text-xs text-muted-foreground italic whitespace-nowrap">Not configured in model</span>
                  : <span className="text-xs text-muted-foreground italic whitespace-nowrap">None selected</span>}
              </div>
              {tzData.length > 0 && (
                <button type="button" onClick={() => setShowTimezoneModal(true)}
                  className="px-3 py-1.5 text-xs bg-sky-500/10 hover:bg-sky-500/20 text-sky-600 dark:text-sky-400 rounded-md border border-sky-500/30 transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                  <Keyboard className="h-3.5 w-3.5" />Edit
                </button>
              )}
            </div>
            <MultiSelectModal title="Select Timezones" description="Choose the timezones supported by this phone model"
              items={modalItems} selectedIds={selectedIds} onSelectionChange={handleChange}
              onClose={() => setShowTimezoneModal(false)} isOpen={showTimezoneModal}
              searchPlaceholder="Search timezones..." icon={Keyboard} />
          </>
        );
      }} />

      {/* Time Formats */}
      <Controller control={control} name="timeFormatOptions" render={({ field }) => {
        const data: { key: string; value?: string; selected: boolean }[] = (() => {
          try { const p = JSON.parse(field.value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; }
        })();
        const selectedIds = data.filter(d => d.selected !== false).map(d => d.key);
        const modalItems = data.map(opt => ({ id: opt.key, name: opt.key }));
        const handleChange = (newIds: string[]) => {
          const s = new Set(newIds);
          field.onChange(JSON.stringify(data.map(item => ({ ...item, selected: s.has(item.key) }))));
        };
        return (
          <>
            <div className="flex items-center gap-2 py-2 border-t border-border/50">
              <span className="text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[180px]">Time Formats</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap flex-shrink-0">{selectedIds.length}/{data.length}</span>
              <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {selectedIds.length > 0 ? selectedIds.map((tf, idx) => (
                  <span key={`tf-${idx}`} className="inline-block px-2 py-0.5 text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded border border-amber-500/20 whitespace-nowrap flex-shrink-0">{tf}</span>
                )) : data.length === 0
                  ? <span className="text-xs text-muted-foreground italic whitespace-nowrap">Not configured in model</span>
                  : <span className="text-xs text-muted-foreground italic whitespace-nowrap">None selected</span>}
              </div>
              {data.length > 0 && (
                <button type="button" onClick={() => setShowTimeFormatModal(true)}
                  className="px-3 py-1.5 text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded-md border border-amber-500/30 transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                  <Keyboard className="h-3.5 w-3.5" />Edit
                </button>
              )}
            </div>
            <MultiSelectModal title="Select Time Formats" description="Choose the time formats supported by this phone model"
              items={modalItems} selectedIds={selectedIds} onSelectionChange={handleChange}
              onClose={() => setShowTimeFormatModal(false)} isOpen={showTimeFormatModal}
              searchPlaceholder="Search time formats..." icon={Keyboard} />
          </>
        );
      }} />

      {/* Date Formats */}
      <Controller control={control} name="dateFormatOptions" render={({ field }) => {
        const data: { key: string; value?: string; selected: boolean }[] = (() => {
          try { const p = JSON.parse(field.value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; }
        })();
        const selectedIds = data.filter(d => d.selected !== false).map(d => d.key);
        const modalItems = data.map(opt => ({ id: opt.key, name: opt.key }));
        const handleChange = (newIds: string[]) => {
          const s = new Set(newIds);
          field.onChange(JSON.stringify(data.map(item => ({ ...item, selected: s.has(item.key) }))));
        };
        return (
          <>
            <div className="flex items-center gap-2 py-2 border-t border-border/50">
              <span className="text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[180px]">Date Formats</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap flex-shrink-0">{selectedIds.length}/{data.length}</span>
              <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {selectedIds.length > 0 ? selectedIds.map((df, idx) => (
                  <span key={`df-${idx}`} className="inline-block px-2 py-0.5 text-xs bg-violet-500/10 text-violet-600 dark:text-violet-400 rounded border border-violet-500/20 whitespace-nowrap flex-shrink-0">{df}</span>
                )) : data.length === 0
                  ? <span className="text-xs text-muted-foreground italic whitespace-nowrap">Not configured in model</span>
                  : <span className="text-xs text-muted-foreground italic whitespace-nowrap">None selected</span>}
              </div>
              {data.length > 0 && (
                <button type="button" onClick={() => setShowDateFormatModal(true)}
                  className="px-3 py-1.5 text-xs bg-violet-500/10 hover:bg-violet-500/20 text-violet-600 dark:text-violet-400 rounded-md border border-violet-500/30 transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                  <Keyboard className="h-3.5 w-3.5" />Edit
                </button>
              )}
            </div>
            <MultiSelectModal title="Select Date Formats" description="Choose the date formats supported by this phone model"
              items={modalItems} selectedIds={selectedIds} onSelectionChange={handleChange}
              onClose={() => setShowDateFormatModal(false)} isOpen={showDateFormatModal}
              searchPlaceholder="Search date formats..." icon={Keyboard} />
          </>
        );
      }} />

      {/* Supported Languages */}
      <Controller control={control} name="languageOptions" render={({ field }) => {
        const data: { value: string; label: string; selected: boolean }[] = (() => {
          try { const p = JSON.parse(field.value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; }
        })();
        const selectedIds = data.filter(l => l.selected !== false).map(l => l.value);
        const modalItems = data.map(opt => ({ id: opt.value, name: opt.label || opt.value }));
        const handleChange = (newIds: string[]) => {
          const s = new Set(newIds);
          field.onChange(JSON.stringify(data.map(item => ({ ...item, selected: s.has(item.value) }))));
        };
        return (
          <>
            <div className="flex items-center gap-2 py-2 border-t border-border/50">
              <span className="text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[180px]">Supported Languages</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap flex-shrink-0">{selectedIds.length}/{data.length}</span>
              <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {selectedIds.length > 0 ? selectedIds.map((code, idx) => (
                  <span key={`lang-${idx}`} className="inline-block px-2 py-0.5 text-xs bg-orange-500/10 text-orange-600 dark:text-orange-400 rounded border border-orange-500/20 whitespace-nowrap flex-shrink-0">
                    {data.find(l => l.value === code)?.label || code}
                  </span>
                )) : data.length === 0
                  ? <span className="text-xs text-muted-foreground italic whitespace-nowrap">Not configured in model</span>
                  : <span className="text-xs text-muted-foreground italic whitespace-nowrap">None selected</span>}
              </div>
              {data.length > 0 && (
                <button type="button" onClick={() => setShowLanguageModal(true)}
                  className="px-3 py-1.5 text-xs bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-md border border-orange-500/30 transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                  <Keyboard className="h-3.5 w-3.5" />Edit
                </button>
              )}
            </div>
            <MultiSelectModal title="Select Languages" description="Choose the languages supported by this phone model"
              items={modalItems} selectedIds={selectedIds} onSelectionChange={handleChange}
              onClose={() => setShowLanguageModal(false)} isOpen={showLanguageModal}
              searchPlaceholder="Search languages..." icon={Keyboard} />
          </>
        );
      }} />
    </FormBox>
  );
}
